/**
 * D227: a verb that stands for a direction (`climb` → `up`) — the one check
 * `compileLexicon` makes of an intent's `bareDirection`, split out of
 * `compile-lexicon.ts` at the 500-line cap.
 *
 * @packageDocumentation
 */

import { type DirectionToken } from '../types.js';
import { normalizeAuthoredToken } from './authored-token.js';
import { type IntentDefinition } from './lexicon-definition.js';

/**
 * D227: validate a verb's `bareDirection` — the direction it stands for
 * alone. Directions are collected before intents, so the declared ones are
 * known here; an alias is refused in favor of the canonical (the lookup the
 * grammar does is by canonical token), and the object policy must leave the
 * verb free to stand alone AND to take an object, or the field says nothing
 * a plainer declaration would not.
 *
 * @param intent - The authored intent
 * @param location - Where the intent is declared (for issue messages)
 * @param directionsByToken - The directions collected so far, canonical tokens and aliases alike
 * @param reportIssue - Where a refused declaration is reported
 * @returns The branded direction, or `undefined` when none is declared or the declaration is refused
 */
export const compileBareDirection = (
	intent: IntentDefinition,
	location: string,
	directionsByToken: ReadonlyMap<string, DirectionToken>,
	reportIssue: (issue: string) => void,
): DirectionToken | undefined => {
	if (intent.bareDirection === undefined) {
		return undefined;
	}

	const directionText = normalizeAuthoredToken(intent.bareDirection);
	const directionToken = directionsByToken.get(directionText);

	if (directionToken === undefined) {
		reportIssue(`${location}: bareDirection "${directionText}" names no declared direction`);

		return undefined;
	}

	if ((directionToken as string) !== directionText) {
		reportIssue(`${location}: bareDirection "${directionText}" is an alias of "${directionToken as string}" — name the canonical direction`);

		return undefined;
	}

	if (intent.objectPolicy !== 'optional') {
		reportIssue(
			intent.objectPolicy === 'forbidden'
				? `${location}: bareDirection on a verb that forbids objects — declare "${intent.canonicalToken}" as an alias of the direction "${directionText}" instead`
				: `${location}: bareDirection on a verb that requires an object — it can never stand alone, so the direction could never be read`,
		);

		return undefined;
	}

	return directionToken;
};
